import { useEffect, useState } from "react";
import { getEmailConfig, testEmailConfig, updateEmailConfig } from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useToast } from "../../components/useToast";

export function EmailConfigPanel() {
  const { data: config, loading, error, refresh } = useAdminResource(() => getEmailConfig(), []);
  const { showSuccess, showError } = useToast();

  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [useTls, setUseTls] = useState(true);
  const [useSsl, setUseSsl] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);

  const [savePending, setSavePending] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testPending, setTestPending] = useState(false);

  useEffect(() => {
    if (!config) return;
    setHost(config.smtp_host ?? "");
    setPort(config.smtp_port ? String(config.smtp_port) : "587");
    setUsername(config.smtp_username ?? "");
    setFromEmail(config.from_email ?? "");
    setFromName(config.from_name ?? "");
    setUseTls(config.use_tls);
    setUseSsl(config.use_ssl);
    setEnabled(config.enabled);
    setHasPassword(config.has_password);
    setPassword(""); // never prefilled -- write-only
  }, [config]);

  function buildPayload() {
    return {
      smtp_host: host.trim() || null,
      smtp_port: port ? Number(port) : null,
      smtp_username: username.trim() || null,
      // Empty means "keep existing" on the backend.
      smtp_password: password ? password : null,
      from_email: fromEmail.trim() || null,
      from_name: fromName.trim() || null,
      use_tls: useTls,
      use_ssl: useSsl,
      enabled,
    };
  }

  async function submitSave() {
    setSavePending(true);
    try {
      await updateEmailConfig(buildPayload());
      showSuccess("Saved SMTP settings");
      setPassword("");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to save SMTP settings");
    } finally {
      setSavePending(false);
    }
  }

  async function submitTest() {
    if (!testTo.trim()) {
      showError("Enter an email address to send the test to");
      return;
    }
    setTestPending(true);
    try {
      // Save first so the test uses exactly what's on screen.
      await updateEmailConfig(buildPayload());
      setPassword("");
      const result = await testEmailConfig(testTo.trim());
      showSuccess(result.detail ?? "Test email sent");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Test send failed");
    } finally {
      setTestPending(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Email / SMTP</h2>
        <span>{enabled ? "Using your SMTP" : "Using shared relay"}</span>
      </header>
      <p className="management-note">
        Send visitor emails from your own mail server. When enabled, all of this company's email
        (invites, approvals, notifications) goes through your SMTP instead of the shared 0todeploy
        relay. Leave disabled to keep using the shared relay. The password is stored encrypted and
        never shown again.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      <form
        className="action-form action-card"
        onSubmit={(event) => {
          event.preventDefault();
          void submitSave();
        }}
      >
        <div className="checkin-fields">
          <input
            value={host}
            onChange={(event) => setHost(event.target.value)}
            placeholder="SMTP host (e.g. smtp.gmail.com)"
            aria-label="SMTP host"
            disabled={loading}
          />
          <input
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(event) => setPort(event.target.value)}
            placeholder="Port (587 TLS / 465 SSL)"
            aria-label="SMTP port"
            disabled={loading}
          />
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Username"
            aria-label="SMTP username"
            autoComplete="off"
            disabled={loading}
          />
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={hasPassword ? "Password (leave blank to keep current)" : "Password"}
            aria-label="SMTP password"
            autoComplete="new-password"
            disabled={loading}
          />
          <input
            value={fromEmail}
            onChange={(event) => setFromEmail(event.target.value)}
            placeholder="From email (e.g. visitors@yourco.com)"
            aria-label="From email"
            disabled={loading}
          />
          <input
            value={fromName}
            onChange={(event) => setFromName(event.target.value)}
            placeholder="From name (e.g. YourCo Reception)"
            aria-label="From name"
            disabled={loading}
          />
        </div>

        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={useTls}
            onChange={(event) => {
              setUseTls(event.target.checked);
              if (event.target.checked) setUseSsl(false);
            }}
          />
          STARTTLS (port 587)
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={useSsl}
            onChange={(event) => {
              setUseSsl(event.target.checked);
              if (event.target.checked) setUseTls(false);
            }}
          />
          Implicit SSL/TLS (port 465)
        </label>
        <label className="checkbox-field">
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          Enable sending via this SMTP server
        </label>

        <button className="refresh-btn action-submit" type="submit" disabled={savePending || loading}>
          {savePending ? "Saving..." : "Save SMTP Settings"}
        </button>
      </form>

      <div className="action-form action-card">
        <label htmlFor="smtp-test-to" className="toolbar-label">
          Send a test email (saves current settings, then sends)
        </label>
        <div className="action-row">
          <input
            id="smtp-test-to"
            value={testTo}
            onChange={(event) => setTestTo(event.target.value)}
            placeholder="you@yourco.com"
            aria-label="Test recipient email"
          />
          <button
            className="secondary-btn"
            type="button"
            onClick={() => void submitTest()}
            disabled={testPending || loading}
          >
            {testPending ? "Sending..." : "Send Test"}
          </button>
        </div>
      </div>
    </article>
  );
}
